import { createContextApi, providesList, createCursorPagedEndpoint, emitPlatformEvent, invalidatesOne, providesOne, registerCrossContextInvalidation } from '@quickbee/store';

// src/index.ts
var PAYMENT_TAGS = ["PaymentOrder", "Quote", "GatewayConfig", "CashPosition", "CashDeposit"];
var QUOTE_ID = "CURRENT";
function gatewayScopeKey(args) {
  if (args?.scope === "PLATFORM") return "platform";
  return args?.tenantId ?? "self";
}
function gatewayScopeParams(args) {
  if (args?.scope === "PLATFORM") return { scope: "PLATFORM" };
  if (args?.tenantId != null) return { tenantId: args.tenantId };
  return {};
}
var paymentEndpoints = (build) => ({
  /**
   * Prices a hypothetical payment. Creates nothing and reserves no rate — the price is only
   * locked when an order is actually created, so this response carries no expiry to honour.
   *
   * Cached for 15s all the same: the underlying gold rate moves, and a breakdown shown next
   * to a "Pay" button should not be minutes old. `pricing.updated` evicts it sooner (see
   * `PAYMENT_INVALIDATION_RULES`).
   */
  getQuote: build.query({
    // Spelled out rather than spread: these three names are the controller's
    // `@RequestParam`s, and `amount`/`purpose` guessed from the arg object is exactly
    // the kind of drift I3 exists to prevent. `undefined` values are dropped by the client.
    query: ({ amountFiat, purpose, channel, method, unit }) => ({
      url: "/payments/quote",
      params: { amountFiat, purpose, channel, method, unit }
    }),
    keepUnusedDataFor: 15,
    providesTags: [{ type: "Quote", id: QUOTE_ID }]
  }),
  /**
   * The payment book, cursor-paginated. Resolves to {@link PaymentOrderSummaryResponse}
   * grid rows — **not** the detail shape `getOrder` returns.
   *
   * Who the rows belong to depends on the caller's effective scope, not on this call:
   * an own-scoped caller always gets their own orders (a supplied `customerId` is
   * ignored, never a 403), a `payment:read:tenant` caller gets one customer's or the
   * whole tenant's. See {@link ListPaymentOrdersArgs}.
   *
   * A back-office screen reads `statusDetail`, not `status` — the latter is the
   * three-value badge the customer-facing clients render, in which
   * `AWAITING_VERIFICATION` is indistinguishable from `CREATED`.
   */
  listOrders: build.query({
    query: ({ size, cursor, sort, purpose, status, customerId }) => ({
      url: "/payments/orders",
      params: { size, cursor, sort, purpose, status, customerId }
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("PaymentOrder", result),
    keepUnusedDataFor: 120
  }),
  /**
   * The same payment book, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listOrders} drops `cursor` from its cache key and appends every response into a single
   * entry, which is exactly right for a "load more" list and exactly wrong for a numbered pager:
   * paging forward would grow the rendered set instead of replacing it, and paging *back* would
   * re-fetch a cursor already walked and append its rows a second time.
   *
   * So this one keeps the cursor in the cache key. Each page is its own entry, which also makes
   * *Previous* free — a page already walked is answered from cache rather than re-fetched. It is
   * what `useCursorPager` reads; see `api-loan`'s `listTenantLeadsPage` and `api-audit`'s
   * `listEntriesPage`, where the same pair exists for the same reason.
   */
  listOrdersPage: build.query({
    query: ({ size, cursor, sort, purpose, status, customerId }) => ({
      url: "/payments/orders",
      params: { size, cursor, sort, purpose, status, customerId }
    }),
    providesTags: (result) => providesList("PaymentOrder", result),
    keepUnusedDataFor: 120
  }),
  /**
   * The cash-verification queue (B3) — collections whose money has arrived and whose
   * gold has not, because approval is the allocating act.
   *
   * Requires `payment:read:tenant`; an `AGENT`, who may record a collection, cannot
   * read it. Cached briefly on purpose: this is a work queue two reviewers may hold
   * open at once, and a stale row is one they both try to approve.
   */
  listOfflineCollections: build.query({
    query: ({ size, cursor, status, channel, collectedByUserId }) => ({
      url: "/payments/offline-collections",
      params: { size, cursor, status, channel, collectedByUserId }
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("PaymentOrder", result),
    keepUnusedDataFor: 30
  }),
  /**
   * The same queue, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listOfflineCollections} drops `cursor` from its cache key and appends every response into
   * a single entry, which is exactly right for a "load more" list and exactly wrong for a numbered
   * pager: paging forward would grow the rendered set instead of replacing it, and paging *back*
   * would re-fetch a cursor already walked and append its rows a second time.
   *
   * So this one keeps the cursor in the cache key. Each page is its own entry, which also makes
   * Previous free — a page already walked is answered from cache rather than re-fetched. It is what
   * `useCursorPager` reads; see `api-lender`'s `listLendersPage`, where the same pair exists for the
   * same reason.
   *
   * POS's collections register is the caller. `PaymentOrderRepositoryAdapter` has paged this read
   * correctly since the queue was built, so the register fetching one window of fifty and paging
   * inside it was never a backend limit — only a screen that never asked for the second page.
   *
   * `apps/operations`' cash queue keeps reading the accumulating sibling; both are the same URL and
   * the same rows, so a verification decision invalidates them together through the shared
   * `PaymentOrder` tag.
   */
  listOfflineCollectionsPage: build.query({
    query: ({ size, cursor, status, channel, collectedByUserId }) => ({
      url: "/payments/offline-collections",
      params: { size, cursor, status, channel, collectedByUserId }
    }),
    providesTags: (result) => providesList("PaymentOrder", result),
    keepUnusedDataFor: 30
  }),
  /**
   * "Who is this, and what can I take money into for them?" — the read both recording screens
   * open with (multi-channel collections §5.3).
   *
   * Exactly one of `phone` / `customerId`, and which one this caller may use follows from their
   * effective scope. Not cached beyond a few seconds: the answer includes which instalment is
   * payable, and a stale one puts a clerk in front of a customer offering an instalment that was
   * settled online a minute ago.
   */
  getCollectionContext: build.query({
    query: (args) => ({ url: "/payments/collection-context", params: { ...args } }),
    keepUnusedDataFor: 5
  }),
  getOrder: build.query({
    query: (id) => ({ url: `/payments/orders/${id}` }),
    providesTags: (_r, _e, id) => providesOne("PaymentOrder", id)
  }),
  /**
   * Returns the gateway order id and the **public** Razorpay `key_id` for Checkout.js — not
   * the order itself. Fetch the order with `getOrder` if the screen needs the full detail.
   */
  createOrder: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/payments/orders",
      method: "POST",
      body,
      idempotencyKey: `payment-order:${body.purpose}:${draftId}`
    }),
    invalidatesTags: [{ type: "PaymentOrder", id: "LIST" }]
  }),
  /**
   * Authoritative checkout verification. The browser is still never trusted for capture
   * (ADR-003) — the server proves the HMAC itself and then asks the gateway directly — so the
   * response normally comes back **`CAPTURED`**, in which case the gold is already credited and
   * there is nothing to wait for. It reads `AUTHORIZED` only on the degraded path (the gateway
   * probe failed, or confirmation is switched off), where the webhook and the reconciliation
   * sweep still finish it and a screen should poll `getOrder` until they do.
   *
   * Act on the returned status rather than assuming either outcome: treating every response as
   * unsettled is what used to put a saver through a minute of spinner for a payment the server
   * had already confirmed.
   *
   * The {@link CaptureContext} fields are client-side only and never sent — the request record
   * has exactly three fields.
   */
  verifyOrder: build.mutation({
    query: ({ id, schemeEnrollmentId: _enrollment, customerId: _customer, ...body }) => ({
      url: `/payments/orders/${id}/verify`,
      method: "POST",
      body,
      idempotencyKey: `payment-verify:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("PaymentOrder", id),
    async onQueryStarted({ schemeEnrollmentId, customerId }, { queryFulfilled }) {
      try {
        const { data } = await queryFulfilled;
        if (data.status === "CAPTURED") {
          emitPlatformEvent("payment.captured", {
            orderId: data.id,
            ...schemeEnrollmentId ? { enrollmentId: schemeEnrollmentId } : {},
            ...customerId ? { customerId } : {}
          });
        }
      } catch {
      }
    }
  }),
  /**
   * The vendor-neutral confirmation — what `verifyOrder` is now the Razorpay-shaped alias of.
   *
   * Razorpay sends its signed callback; Cashfree sends `{ provider: 'CASHFREE' }` and nothing
   * else, because its sheet returns nothing trustworthy and the server asks the gateway instead.
   * Same endpoint, same guarantees, same cache effects — only what the browser is trusted for
   * differs, and for Cashfree it is trusted for nothing at all.
   *
   * Prefer this over `verifyOrder` in new code; `verifyOrder` stays because three shipped apps
   * call it and the native one ships on a store cadence the backend does not control.
   */
  confirmOrder: build.mutation({
    query: ({ id, schemeEnrollmentId: _enrollment, customerId: _customer, ...body }) => ({
      url: `/payments/orders/${id}/confirm`,
      method: "POST",
      body,
      // The same key shape as verifyOrder's, and deliberately: the two endpoints confirm the same
      // order, and a client that retried through the other one must not get a second in flight.
      idempotencyKey: `payment-verify:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("PaymentOrder", id),
    async onQueryStarted({ schemeEnrollmentId, customerId }, { queryFulfilled }) {
      try {
        const { data } = await queryFulfilled;
        if (data.status === "CAPTURED") {
          emitPlatformEvent("payment.captured", {
            orderId: data.id,
            ...schemeEnrollmentId ? { enrollmentId: schemeEnrollmentId } : {},
            ...customerId ? { customerId } : {}
          });
        }
      } catch {
      }
    }
  }),
  /**
   * RM/tenant-admin only (`payment:refund:tenant`); there is no customer-facing refund in v1.
   *
   * `draftId` — not the amount — scopes the key, because the backend turns this key into the
   * refund's identity. Keying on the amount would make a second, genuinely intended refund of
   * the same amount silently resolve to the first one.
   */
  refundOrder: build.mutation({
    query: ({ id, draftId, ...body }) => ({
      url: `/payments/orders/${id}/refunds`,
      method: "POST",
      body,
      idempotencyKey: `payment-refund:${id}:${draftId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("PaymentOrder", id),
    async onQueryStarted({ id }, { queryFulfilled }) {
      try {
        const { data } = await queryFulfilled;
        emitPlatformEvent("payment.refunded", { orderId: id, refundId: data.id });
      } catch {
      }
    }
  }),
  /**
   * The *maker* half of the maker–checker control (`payment:collect:tenant`): records money
   * an agent took in the field or a customer wired direct. Creates an order in
   * `AWAITING_VERIFICATION` — no gold is allocated until a reviewer confirms receipt.
   */
  recordOfflineCollection: build.mutation({
    query: ({ draftId, attempt, ...body }) => ({
      url: "/payments/offline-collections",
      method: "POST",
      body,
      // PER ATTEMPT, not per drawer. This is the one endpoint on the platform where a
      // drawer-stable key is actively harmful: `IdempotencyFilter` stores the response body
      // *after* `copyBodyToResponse()` has drained it, so every replay returns 2xx with zero
      // bytes. A drawer-stable key therefore turns the first failure from any cause — a
      // network blip, a 503, a rejected validation — into a permanent one: every "try again"
      // replays the spent key and the receipt panel reads fields off `null`. That is the
      // tenant-document-upload post-mortem, and this mutation is where it would have recurred.
      //
      // Retrying a genuinely-committed collection is still safe: the server's own
      // (tenant, idempotency_key) unique index is gone, but `paymentOrderId` is server-minted
      // per request and the scheme/instalment gate refuses a second charge on a settled
      // instalment. The receipt is read back with `getOrder`, never out of this response.
      idempotencyKey: `offline-collection:${draftId}:${attempt}`
    }),
    invalidatesTags: [{ type: "PaymentOrder", id: "LIST" }]
  }),
  /**
   * The *checker* half (`payment:verify:tenant`, a deliberately separate grant). Approval is
   * what allocates gold — the order goes straight to `CAPTURED`, so this emits the same
   * cross-context event the gateway path does. Rejection lands on `VERIFICATION_REJECTED`.
   *
   * Keyed on the order alone: a reviewer decides once, and both outcomes are terminal.
   */
  verifyOfflineCollection: build.mutation({
    query: ({ id, schemeEnrollmentId: _enrollment, customerId: _customer, ...body }) => ({
      url: `/payments/offline-collections/${id}/verification`,
      method: "POST",
      body,
      idempotencyKey: `offline-collection-verify:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("PaymentOrder", id),
    async onQueryStarted({ schemeEnrollmentId, customerId }, { queryFulfilled }) {
      try {
        const { data } = await queryFulfilled;
        if (data.status === "CAPTURED") {
          emitPlatformEvent("payment.captured", {
            orderId: data.id,
            ...schemeEnrollmentId ? { enrollmentId: schemeEnrollmentId } : {},
            ...customerId ? { customerId } : {}
          });
        }
      } catch {
      }
    }
  }),
  // ─── Gateway configuration (admin console, design §12/§16) ──────────────────
  /**
   * The addressed scope's rows plus the platform row, with the `effective`
   * marker and masked identifiers. Cached per scope key; no polling — a config
   * changes when an operator changes it.
   */
  getGatewayConfig: build.query({
    query: (args) => ({
      url: "/payments/gateway-config",
      params: gatewayScopeParams(args ?? void 0)
    }),
    providesTags: (_r, _e, args) => [
      { type: "GatewayConfig", id: gatewayScopeKey(args ?? void 0) }
    ],
    keepUnusedDataFor: 60
  }),
  /**
   * Configure (or re-configure) the addressed scope's Razorpay account. The
   * backend probes the submitted credentials before storing anything — a 422
   * (`GATEWAY_PROBE_FAILED`) means nothing changed. `credentials` omitted means
   * a settings-only change against the stored secret.
   *
   * The idempotency key carries the scope key *and* the drawer's draft id: the
   * drawer can switch scope while open, and without the discriminator a
   * platform-scope submit's stored response would replay for a tenant-scope one.
   *
   * Addressing params are derived from the body's own scope fields so query
   * string and body can never disagree.
   */
  configureGateway: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/payments/gateway-config",
      method: "POST",
      body,
      params: gatewayScopeParams(body),
      idempotencyKey: `payment-gateway-configure:${gatewayScopeKey(body)}:${draftId}`
    }),
    // The bare type: a platform-row change alters what every tenant-scoped read
    // resolves as `effective`, so one scope entry is not enough.
    invalidatesTags: ["GatewayConfig"]
  }),
  /**
   * Deactivate one config row. 204. Not `@IdempotentRequest` server-side (comms
   * precedent), so the client skips the key rather than minting one the filter
   * would reject.
   */
  deactivateGatewayConfig: build.mutation({
    query: ({ id, ...scope }) => ({
      url: `/payments/gateway-config/${id}/deactivate`,
      method: "POST",
      params: gatewayScopeParams(scope),
      skipIdempotencyKey: true
    }),
    invalidatesTags: ["GatewayConfig"]
  }),
  /**
   * On-demand probe of the addressed scope's *stored* credential. Read-only at
   * the vendor, hence `skipIdempotencyKey`; failures surface as typed 422s
   * (`GATEWAY_NOT_CONFIGURED` / `GATEWAY_PROBE_FAILED`), not `ok: false`.
   */
  probeGateway: build.mutation({
    query: (args) => ({
      url: "/payments/gateway-config/probe",
      method: "POST",
      params: gatewayScopeParams(args ?? void 0),
      skipIdempotencyKey: true
    })
  }),
  /**
   * Issue a fresh webhook HMAC secret for the addressed scope's ACTIVE row. The response
   * is the ONE time the secret is readable — the drawer shows it with a copy button and
   * the URL + events to paste beside it. The previous secret stops verifying at once.
   *
   * Idempotent per intent (`draftId` is minted when the confirm dialog opens): a retried
   * click replays the same issued secret instead of rotating twice and showing a value
   * the vault no longer holds. The scope key discriminates like `configureGateway`.
   */
  rotateGatewayWebhookSecret: build.mutation({
    query: ({ draftId, ...scope }) => ({
      url: "/payments/gateway-config/webhook-secret/rotate",
      method: "POST",
      params: gatewayScopeParams(scope),
      idempotencyKey: `payment-gateway-webhook-rotate:${gatewayScopeKey(scope)}:${draftId}`
    }),
    invalidatesTags: ["GatewayConfig"]
  }),
  // ── Cash custody (design §5.8 / G11) ──────────────────────────────────────
  //
  // The gap these close: verifying a collection asks "did this happen" and is bounded by the
  // price lock, so it takes hours. Banking asks "did the money come back" and takes days. In
  // between, gold has been allocated against notes in somebody's bag.
  /**
   * Un-banked field cash per collector, largest exposure first.
   *
   * Omit `collectorUserId` for the fleet view — only a caller holding
   * `payment:cash-custody:tenant` may do that, and a narrower caller gets a 403 rather than
   * being quietly narrowed to their own row. A collector holding nothing still gets a row when
   * they ask about themselves: "you have banked everything" and a blank screen are different
   * messages, and only one is true.
   */
  listCashPositions: build.query({
    query: (args) => ({
      url: "/payments/cash-custody/positions",
      // Always an object: `exactOptionalPropertyTypes` refuses an undefined `params`, and an
      // undefined VALUE inside it is dropped by the serialiser, which is the fleet read.
      params: { collectorUserId: args?.collectorUserId }
    }),
    providesTags: [{ type: "CashPosition", id: "LIST" }],
    // Short: the position moves with every collection an agent records, and a stale figure is
    // what a float ceiling would be enforced against on the screen while the server refuses.
    keepUnusedDataFor: 15
  }),
  /** The collections behind a position — the picker a deposit is declared from. */
  listUnbankedCollections: build.query({
    query: ({ size, cursor, collectorUserId }) => ({
      url: "/payments/cash-custody/unbanked",
      params: { size, cursor, collectorUserId }
    }),
    ...createCursorPagedEndpoint(),
    providesTags: [{ type: "CashPosition", id: "LIST" }],
    keepUnusedDataFor: 15
  }),
  /** Declared deposits — the reconciliation queue, and a collector's own history. */
  listCashDeposits: build.query({
    query: ({ size, cursor, collectorUserId, status }) => ({
      url: "/payments/cash-custody/deposits",
      params: { size, cursor, collectorUserId, status }
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("CashDeposit", result),
    keepUnusedDataFor: 30
  }),
  /**
   * The same read, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listCashDeposits} drops `cursor` from its cache key and appends every response into a
   * single entry, which is right for a "load more" list and wrong for a numbered pager: paging
   * forward would grow the rendered set rather than replace it, and paging *back* would re-fetch
   * a walked cursor and append its rows a second time. This one keeps the cursor in the cache
   * key, so each page is its own entry and Previous is answered from cache.
   *
   * Same pair, same reason, as `api-audit`'s `listEntries`/`listEntriesPage`.
   */
  listCashDepositsPage: build.query({
    query: ({ size, cursor, collectorUserId, status }) => ({
      url: "/payments/cash-custody/deposits",
      params: { size, cursor, collectorUserId, status }
    }),
    providesTags: (result) => providesList("CashDeposit", result),
    keepUnusedDataFor: 30
  }),
  /** One deposit and the collections it claims — what a reviewer reads before ruling. */
  getCashDeposit: build.query({
    query: (id) => ({ url: `/payments/cash-custody/deposits/${id}` }),
    providesTags: (_result, _error, id) => [{ type: "CashDeposit", id }]
  }),
  /**
   * The *maker* half of the custody control: the collector's claim to have banked the cash.
   *
   * Per-attempt idempotency, as `recordOfflineCollection` — and for the same reason. A
   * drawer-stable key turns the first failure from any cause into a permanent one, because
   * `IdempotencyFilter` stores the response body after `copyBodyToResponse()` has drained it
   * and every replay then returns 2xx with zero bytes.
   */
  declareCashDeposit: build.mutation({
    query: ({ draftId, attempt, ...body }) => ({
      url: "/payments/cash-custody/deposits",
      method: "POST",
      body,
      idempotencyKey: `cash-deposit:${draftId}:${attempt}`
    }),
    // Both: declaring relieves the position AND adds a slip to the queue.
    invalidatesTags: [
      { type: "CashPosition", id: "LIST" },
      { type: "CashDeposit", id: "LIST" }
    ]
  }),
  /**
   * The *checker* half (`payment:cash-reconcile:tenant`, a deliberately separate grant, so the
   * separation is a matter of granted authority and not of who clicks which screen).
   *
   * Refusing releases the covered collections back onto the collector's position, so this
   * invalidates the position list as well as the slip.
   */
  reconcileCashDeposit: build.mutation({
    query: ({ id, draftId, ...body }) => ({
      url: `/payments/cash-custody/deposits/${id}/reconciliation`,
      method: "POST",
      body,
      // Drawer-stable here, unlike the declaration: a reviewer decides once and both outcomes
      // are terminal, so a replayed key can only ever return the decision that was already made.
      idempotencyKey: `cash-deposit-reconcile:${id}:${draftId}`
    }),
    invalidatesTags: (_result, _error, { id }) => [
      { type: "CashDeposit", id },
      { type: "CashDeposit", id: "LIST" },
      { type: "CashPosition", id: "LIST" }
    ]
  })
});

// src/collections.ts
var COLLECTION_METHODS = {
  COUNTER: ["CASH", "UPI"],
  FIELD_AGENT: ["CASH", "UPI"],
  DIRECT_BANK_TRANSFER: ["BANK_TRANSFER", "UPI"]
};
function isServerMintedReference(channel, method) {
  return channel === "COUNTER" && method === "CASH";
}

// src/types.ts
var PAYMENT_MAX_PAGE_SIZE = 100;

// src/index.ts
var paymentApi = createContextApi({
  context: "payment",
  reducerPath: "paymentApi",
  tagTypes: PAYMENT_TAGS,
  endpoints: paymentEndpoints
});
var PAYMENT_INVALIDATION_RULES = [
  {
    event: "pricing.updated",
    invalidate: () => [{ type: "Quote", id: QUOTE_ID }],
    settleDelayMs: 0
  }
];
function registerPaymentInvalidation(store) {
  return registerCrossContextInvalidation({
    store,
    api: paymentApi,
    rules: PAYMENT_INVALIDATION_RULES
  });
}
var {
  useGetQuoteQuery,
  useLazyGetQuoteQuery,
  useListOrdersQuery,
  // The discrete twin: one page per cache entry, for a numbered pager (`useCursorPager`).
  useListOrdersPageQuery,
  useListOfflineCollectionsQuery,
  useListOfflineCollectionsPageQuery,
  useGetCollectionContextQuery,
  useLazyGetCollectionContextQuery,
  useGetOrderQuery,
  useLazyGetOrderQuery,
  useCreateOrderMutation,
  useVerifyOrderMutation,
  useConfirmOrderMutation,
  useRefundOrderMutation,
  useRecordOfflineCollectionMutation,
  useVerifyOfflineCollectionMutation,
  useGetGatewayConfigQuery,
  useLazyGetGatewayConfigQuery,
  useConfigureGatewayMutation,
  useDeactivateGatewayConfigMutation,
  useProbeGatewayMutation,
  useRotateGatewayWebhookSecretMutation,
  // Cash custody (design §5.8 / G11)
  useListCashPositionsQuery,
  useLazyListCashPositionsQuery,
  useListUnbankedCollectionsQuery,
  useListCashDepositsQuery,
  useListCashDepositsPageQuery,
  useGetCashDepositQuery,
  useDeclareCashDepositMutation,
  useReconcileCashDepositMutation
} = paymentApi;

export { COLLECTION_METHODS, PAYMENT_INVALIDATION_RULES, PAYMENT_MAX_PAGE_SIZE, PAYMENT_TAGS, QUOTE_ID, gatewayScopeKey, isServerMintedReference, paymentApi, registerPaymentInvalidation, useConfigureGatewayMutation, useConfirmOrderMutation, useCreateOrderMutation, useDeactivateGatewayConfigMutation, useDeclareCashDepositMutation, useGetCashDepositQuery, useGetCollectionContextQuery, useGetGatewayConfigQuery, useGetOrderQuery, useGetQuoteQuery, useLazyGetCollectionContextQuery, useLazyGetGatewayConfigQuery, useLazyGetOrderQuery, useLazyGetQuoteQuery, useLazyListCashPositionsQuery, useListCashDepositsPageQuery, useListCashDepositsQuery, useListCashPositionsQuery, useListOfflineCollectionsPageQuery, useListOfflineCollectionsQuery, useListOrdersPageQuery, useListOrdersQuery, useListUnbankedCollectionsQuery, useProbeGatewayMutation, useReconcileCashDepositMutation, useRecordOfflineCollectionMutation, useRefundOrderMutation, useRotateGatewayWebhookSecretMutation, useVerifyOfflineCollectionMutation, useVerifyOrderMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map